import { createContext, ReactNode, useCallback, useContext, useMemo, useReducer } from "react";
import conversationsSlice, { DbtDocsState } from "./DbtDocsReducer";
import { UnknownAction } from "@reduxjs/toolkit";
import { ConversationSources } from "./conversations/context/types";

interface ContextProps {
  state: DbtDocsState;
  dispatch: React.Dispatch<UnknownAction>;
  getValue: (callback: (state: DbtDocsState) => unknown) => unknown;
}

export const DbtDocsCollaborationContext = createContext<ContextProps>({
  state: conversationsSlice.getInitialState(),
  dispatch: () => null,
  getValue: () => null,
});

const DbtDocsCollaborationProvider = ({
  children,
  shareId,
  userId,
  conversationGroupId,
  source,
}: {
  children: ReactNode;
  shareId: number;
  userId?: number;
  conversationGroupId?: number;
  source: ConversationSources;
}) => {
  const [state, dispatch] = useReducer(conversationsSlice.reducer, {
    ...conversationsSlice.getInitialState(),
    shareId,
    currentUserId: userId,
    selectedConversationId: conversationGroupId,
    isRightPanelOpen: Boolean(conversationGroupId),
    source,
  });

  const getValue = useCallback(
    (callback: (state: DbtDocsState) => unknown) => {
      return callback(state as DbtDocsState);
    },
    [state]
  );

  const values = useMemo(
    () => ({
      state,
      dispatch,
      getValue,
    }),
    [state, dispatch, getValue]
  );

  return (
    <DbtDocsCollaborationContext.Provider value={values}>
      {children}
    </DbtDocsCollaborationContext.Provider>
  );
};

export default DbtDocsCollaborationProvider;

export const useDbtDocsCollaborationContext = () => {
  return useContext(DbtDocsCollaborationContext);
};

export const useDbtDocsCollaborationSelector = (callback: (state: DbtDocsState) => unknown) => {
  const { getValue } = useContext(DbtDocsCollaborationContext);
  return getValue(callback);
};

export const useDbtDocsCollaborationDispatch = () => {
  const { dispatch } = useContext(DbtDocsCollaborationContext);
  return dispatch;
};
